import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../hooks/store.hook";
import { useDebounce } from "use-debounce";
import {
  setSearchValuePendingFinalFeedback,
  setSelectedPracticePendingFinalFeedback,
  setSearchPracticeValuePendingFinalFeedback,
  setPageNumberPendingFinalFeedback,
} from "../../../../../stores/actions/management.actions";
import { DoubleStructure } from "../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  AlignmentEnum,
  FontWeightEnum,
  PageNames,
} from "../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import SearchInput from "../../../../../components/core/SearchInput/SearchInput";
import {
  COLLABORATOR_LABEL,
  COLLABORATORS_LABEL,
  EMPTY_ARCHIVED_PENDING_MESSEGE,
  NAME_SURNAME_EMAIL_LABEL,
  PRACTICE_LABEL,
} from "../../../../../constants/Dictionary";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import {
  Table,
  TableThemeEnum,
} from "../../../../../components/core/Table/Table";
import { getArchivingPendingTableColumns } from "../../../../../constants/TableColumns";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../constants/AppConstants";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import emptyArchivedPendingImage from "../../../../../images/archivedPending.png";
import {
  StyledFinalFeedbackPendingContainer,
  StyledSearchSelectContainer,
} from "./PendingFinalFeedback.styled";
import { ConfigCollaborator } from "../../../../../types/api/configurations.type";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { StyledLoadingContainer } from "../../../Management.styled";
import { usePendingFinalFeedbackCollaborators } from "../../../../../hooks/manageArchiving.hook";
import {
  DETAIL_QUERY_PARAM_KEY,
  encodeParams,
} from "../../../../../utils/queryParams.utils";

export const PendingFinalFeedback = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [hasData, setHasData] = useState<boolean | null>(null);

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const {
    searchValuePendingFinalFeedback,
    selectedPracticePendingFinalFeedback,
    searchPracticeValuePendingFinalFeedback,
    pageNumberPendingFinalFeedback,
  } = useAppSelector((state) => state.management);

  const [searchDebouncedValue] = useDebounce(
    searchValuePendingFinalFeedback,
    500,
  );

  const isFirstRender = useRef(true);

  const practiceList = orgSettings.areaList.filtered.map(
    (item) => item.practiceList,
  )[0];

  const { data, isFetching } = usePendingFinalFeedbackCollaborators({
    pageNumberPendingFinalFeedback,
    searchDebouncedValue,
    selectedPracticePendingFinalFeedback,
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPageNumberPendingFinalFeedback(0));
  }, [searchDebouncedValue, selectedPracticePendingFinalFeedback, dispatch]);

  useEffect(() => {
    if (data && hasData === null) {
      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);
    }
  }, [data, hasData]);

  const filteredPracticeOptions =
    searchPracticeValuePendingFinalFeedback && practiceList
      ? practiceList.filter((option) =>
          option.name
            .toLowerCase()
            .includes(searchPracticeValuePendingFinalFeedback.toLowerCase()),
        )
      : practiceList || [];

  return (
    <StyledFinalFeedbackPendingContainer>
      {isFetching && hasData === null ? (
        <StyledLoadingContainer>
          <Loader loading={true} />
        </StyledLoadingContainer>
      ) : null}
      {hasData ? (
        <>
          <DoubleStructure
            verticalAlignment={AlignmentEnum.CENTER}
            variant="primary"
          >
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {data &&
                `${data.pagination.total} ${data.pagination.total === 1 ? COLLABORATOR_LABEL : COLLABORATORS_LABEL}`}
            </BodyTyp>
            <StyledSearchSelectContainer>
              <SearchInput
                value={searchValuePendingFinalFeedback}
                onChange={(value) =>
                  dispatch(setSearchValuePendingFinalFeedback(value))
                }
                label={NAME_SURNAME_EMAIL_LABEL}
                name={"search"}
              />
              <Select
                variant={SelectVariantEnum.SINGLE_SELECT}
                label={PRACTICE_LABEL}
                options={filteredPracticeOptions.map((items) => ({
                  value: items.id,
                  label: items.name,
                }))}
                onChange={(value) =>
                  dispatch(setSelectedPracticePendingFinalFeedback(value))
                }
                value={selectedPracticePendingFinalFeedback}
                withSearch
                onSearch={(value) =>
                  dispatch(setSearchPracticeValuePendingFinalFeedback(value))
                }
                searchValue={searchPracticeValuePendingFinalFeedback}
              />
            </StyledSearchSelectContainer>
          </DoubleStructure>
          <Table<ConfigCollaborator>
            loading={isFetching}
            headers={getArchivingPendingTableColumns(
              orgSettings.areaList.all,
              orgSettings.cmsheet,
            )}
            scrollY={"calc(100dvh - 370px)"}
            data={data?.data ?? []}
            clickableRow={(record) =>
              !profile.restrictedCollaboratorIds.includes(record.id)
            }
            onRow={(record) => {
              record.id === profile.id
                ? navigate(`${pathname}${PageNames.PERSONAL_PROFILE}`)
                : navigate(
                    `${pathname}/collaborators/${record.id}?${DETAIL_QUERY_PARAM_KEY}=${encodeParams(
                      {
                        collaboratorName: record.firstName,
                        collaboratorSurname: record.lastName,
                      },
                    )}`,
                  );
            }}
            pagination={{
              current: pageNumberPendingFinalFeedback + 1,
              onChange: (pageNumber) =>
                dispatch(setPageNumberPendingFinalFeedback(pageNumber - 1)),
              total: data?.pagination.total ?? 0,
              pageSize: COLLABORATORS_PAGE_SIZE,
            }}
            theme={TableThemeEnum.DARK}
          />
        </>
      ) : null}
      {!isFetching && hasData === false ? (
        <div className="empty-state">
          <EmptyState
            label={EMPTY_ARCHIVED_PENDING_MESSEGE}
            imageSrc={emptyArchivedPendingImage}
          />
        </div>
      ) : null}
    </StyledFinalFeedbackPendingContainer>
  );
};
